/**
 * Reading an iTunes `Library.xml`.
 *
 * The file is an Apple plist, and a real one is large: the library this was
 * written against is 70 MB, 41,038 tracks and 105 playlists. That size is the
 * only reason this is a hand-written scanner rather than four lines of
 * `DOMParser`. A DOM of a 70 MB plist is somewhere north of half a gigabyte of
 * nodes, most of it fields we do not want; this walks the text once with a
 * single regex and keeps only the dozen keys the importer reads, which comes
 * out at a few megabytes of objects.
 *
 * It is deliberately not a general plist parser. It understands exactly the
 * shape iTunes writes - a root dict with `Tracks` (a dict of dicts) and
 * `Playlists` (an array of dicts) - and skips everything else, nested or not.
 * Anything unexpected is ignored rather than thrown at: a library that has one
 * odd key should still import.
 *
 * See docs/ITUNES-IMPORT.md for what the importer does with all this.
 */

/** A track from the `Tracks` dict, reduced to what the importer reads. */
export interface ItunesTrack {
  /** `Track ID`, as a string - it is a key in the Tracks dict. */
  id: string;
  /** `Persistent ID`. Stable across libraries; only useful for diagnostics. */
  persistentId?: string;
  name: string;
  artist?: string;
  album?: string;
  albumArtist?: string;
  genre?: string;
  /** `Total Time`, milliseconds - the same unit Fadila's `Track.length` uses. */
  duration?: number;
  /** `Size`, bytes. */
  size?: number;
  /**
   * `Rating`, 0-100 in steps of 20, so 80 is four stars. Not used by the
   * import itself; kept because it is what the curation actually lives in
   * (docs/ITUNES-IMPORT.md section 6).
   */
  rating?: number;
  /** True when the rating is inherited from the album rather than set. */
  ratingComputed?: boolean;
  /**
   * `Location`, decoded and with the `file://localhost/` prefix removed, so
   * `D:/Music/Archive/Music/Foo/01 Bar.mp3`. Absent on a stream or a track
   * whose file iTunes had lost.
   */
  location?: string;
  /** `Track Type` is `File`. The other value in the wild is `URL`, a stream. */
  isFile: boolean;
}

/** A playlist from the `Playlists` array. */
export interface ItunesPlaylist {
  /** `Playlist Persistent ID`. */
  id: string;
  name: string;
  /** `Parent Persistent ID` - set on a playlist inside a folder. */
  parentId?: string;
  /** A folder holding other playlists. iTunes gives it its children's items. */
  isFolder: boolean;
  /** Has `Smart Criteria`: the membership came from a rule, not by hand. */
  isSmart: boolean;
  /** Has `Distinguished Kind`: one of iTunes' own (Podcasts, Movies, ...). */
  isDistinguished: boolean;
  /** `Master`: the "Library" playlist, everything you own. */
  isMaster: boolean;
  /** `Track ID`s in playlist order, with duplicates kept. */
  trackIds: string[];
}

export interface ItunesLibrary {
  /** `Music Folder`, decoded like `ItunesTrack.location`. */
  musicFolder?: string;
  /** iTunes' own version string, for the report. */
  applicationVersion?: string;
  tracks: Map<string, ItunesTrack>;
  playlists: ItunesPlaylist[];
}

/**
 * Not a general XML unescape - a plist only ever carries these five plus
 * numeric character references.
 */
const entities: { [entity: string]: string } = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
};

export function decodeXmlText(text: string) {
  if (text.indexOf('&') === -1) {
    return text;
  }

  return text.replace(/&(?:amp|lt|gt|quot|apos|#\d+|#[xX][0-9a-fA-F]+);/g, (entity) => {
    const known = entities[entity];

    if (known) {
      return known;
    }

    const numeric = entity.slice(2, -1);

    const code =
      numeric[0] === 'x' || numeric[0] === 'X'
        ? parseInt(numeric.slice(1), 16)
        : parseInt(numeric, 10);

    return isNaN(code) ? entity : String.fromCodePoint(code);
  });
}

/**
 * `file://localhost/D:/Music/x%20y.mp3` -> `D:/Music/x y.mp3`.
 *
 * A percent sequence that is not valid UTF-8 makes `decodeURIComponent` throw,
 * and a library assembled over fifteen years has a few. The raw form is more
 * use than nothing, so that is what a failure falls back to.
 */
export function decodeLocation(location: string) {
  const withoutScheme = location.replace(/^file:\/\/localhost\//, '').replace(/^file:\/\//, '');

  try {
    return decodeURIComponent(withoutScheme);
  } catch {
    return withoutScheme;
  }
}

/** The file name, extension dropped: `01 Bar.mp3` -> `01 Bar`. */
export function fileNameFromLocation(location: string) {
  const name = location.slice(location.lastIndexOf('/') + 1);

  return name.replace(/\.(?:.(?!\.))+$/, '');
}

/*
  The scanner.

  One regex over the whole text. Each match is an element's opening or closing
  tag plus whatever text follows it up to the next `<` - which for a value
  element is the value, and for everything else is the whitespace between
  elements, which we ignore.

  `[^<]*` is safe as the value: XML forbids a raw `<` in character data, and
  the entities that stand in for it hold no `<` either.
*/
const tokenPattern =
  /<(\/?)(dict|array|key|string|integer|date|real|data|true|false)\s*(\/?)>([^<]*)/g;

/**
 * What the scanner is inside of. One frame per open `dict` or `array`, so the
 * decision of what a container *means* is made once, when it opens, from its
 * parent and the key that introduced it - and nothing has to count depth.
 *
 * `ignored` swallows its own children, which is how the whole of `Smart Info`,
 * `Tracks`' sibling sections and any future key stay out of the way.
 */
type Frame =
  | { kind: 'root' }
  | { kind: 'tracks' }
  | { kind: 'track'; track: ItunesTrack }
  | { kind: 'playlists' }
  | { kind: 'playlist'; playlist: ItunesPlaylist }
  | { kind: 'playlistItems'; playlist: ItunesPlaylist }
  | { kind: 'playlistItem'; playlist: ItunesPlaylist }
  | { kind: 'ignored' };

interface StackEntry {
  frame: Frame;
  /** The `<key>` seen but not yet consumed by a value or a container. */
  key?: string;
}

function newTrack(id: string): ItunesTrack {
  return { id, name: '', isFile: false };
}

function newPlaylist(): ItunesPlaylist {
  return {
    id: '',
    name: '',
    isFolder: false,
    isSmart: false,
    isDistinguished: false,
    isMaster: false,
    trackIds: [],
  };
}

/** Decides what an opening `dict`/`array` means from where it opens. */
function childFrame(parent: StackEntry, tag: 'dict' | 'array'): Frame {
  const { frame, key } = parent;

  switch (frame.kind) {
    case 'root':
      if (key === 'Tracks' && tag === 'dict') return { kind: 'tracks' };
      if (key === 'Playlists' && tag === 'array') return { kind: 'playlists' };
      return { kind: 'ignored' };

    case 'tracks':
      // The key is the track id, and it is repeated inside as `Track ID`.
      return tag === 'dict' && key ? { kind: 'track', track: newTrack(key) } : { kind: 'ignored' };

    case 'playlists':
      return tag === 'dict' ? { kind: 'playlist', playlist: newPlaylist() } : { kind: 'ignored' };

    case 'playlist':
      return key === 'Playlist Items' && tag === 'array'
        ? { kind: 'playlistItems', playlist: frame.playlist }
        : { kind: 'ignored' };

    case 'playlistItems':
      return tag === 'dict'
        ? { kind: 'playlistItem', playlist: frame.playlist }
        : { kind: 'ignored' };

    default:
      return { kind: 'ignored' };
  }
}

function assignTrackField(track: ItunesTrack, key: string, raw: string, tag: string) {
  switch (key) {
    case 'Name':
      track.name = decodeXmlText(raw);
      break;
    case 'Artist':
      track.artist = decodeXmlText(raw);
      break;
    case 'Album':
      track.album = decodeXmlText(raw);
      break;
    case 'Album Artist':
      track.albumArtist = decodeXmlText(raw);
      break;
    case 'Genre':
      track.genre = decodeXmlText(raw);
      break;
    case 'Persistent ID':
      track.persistentId = raw;
      break;
    case 'Total Time':
      track.duration = parseInt(raw, 10) || undefined;
      break;
    case 'Size':
      track.size = parseInt(raw, 10) || undefined;
      break;
    case 'Rating':
      track.rating = parseInt(raw, 10);
      break;
    case 'Rating Computed':
      track.ratingComputed = tag === 'true';
      break;
    case 'Track Type':
      track.isFile = raw === 'File';
      break;
    case 'Location':
      track.location = decodeLocation(decodeXmlText(raw));
      break;
    default:
      break;
  }
}

function assignPlaylistField(pl: ItunesPlaylist, key: string, raw: string, tag: string) {
  switch (key) {
    case 'Name':
      pl.name = decodeXmlText(raw);
      break;
    case 'Playlist Persistent ID':
      pl.id = raw;
      break;
    case 'Parent Persistent ID':
      pl.parentId = raw;
      break;
    case 'Folder':
      pl.isFolder = tag === 'true';
      break;
    // Presence is the signal; iTunes writes the rule itself as base64 data.
    case 'Smart Criteria':
      pl.isSmart = true;
      break;
    case 'Distinguished Kind':
      pl.isDistinguished = true;
      break;
    case 'Master':
      pl.isMaster = tag === 'true';
      break;
    default:
      break;
  }
}

/**
 * How many characters to scan before handing the thread back. Tuned on the
 * 70 MB reference library: about 25 slices, each a few tens of milliseconds,
 * so a progress bar moves and the tab still answers clicks.
 */
const sliceLength = 3000000;

/**
 * Parses the library, pausing every few megabytes.
 *
 * Yields the fraction done (0..1) at each pause and returns the library. The
 * caller decides how to give the thread back between slices - see
 * `parseItunesLibraryAsync`, which is what the UI uses. A generator rather
 * than a callback so the parse can also be run straight through in a test,
 * with `drainParser`.
 */
export function* parseItunesLibrary(xml: string): Generator<number, ItunesLibrary, void> {
  const tracks = new Map<string, ItunesTrack>();
  const playlists: ItunesPlaylist[] = [];

  let musicFolder: string | undefined;
  let applicationVersion: string | undefined;

  const stack: StackEntry[] = [];
  // Own instance: the pattern is module-level and `lastIndex` is state.
  const pattern = new RegExp(tokenPattern.source, 'g');

  let nextPause = sliceLength;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(xml)) !== null) {
    const closing = match[1] === '/';
    const tag = match[2];
    const selfClosing = match[3] === '/';
    const text = match[4];

    if (tag === 'dict' || tag === 'array') {
      if (closing) {
        const done = stack.pop();

        if (done) {
          if (done.frame.kind === 'track') {
            tracks.set(done.frame.track.id, done.frame.track);
          } else if (done.frame.kind === 'playlist') {
            playlists.push(done.frame.playlist);
          }
        }
      } else if (!selfClosing) {
        const parent = stack[stack.length - 1];

        stack.push({
          frame: parent ? childFrame(parent, tag) : { kind: 'root' },
        });

        if (parent) {
          parent.key = undefined;
        }
      }
    } else if (tag === 'key') {
      if (!closing) {
        const top = stack[stack.length - 1];

        if (top) {
          top.key = decodeXmlText(text);
        }
      }
    } else if (!closing) {
      // A value element: string, integer, date, real, data, true or false.
      const top = stack[stack.length - 1];

      if (top && top.key !== undefined) {
        const key = top.key;
        top.key = undefined;

        switch (top.frame.kind) {
          case 'root':
            if (key === 'Music Folder') {
              musicFolder = decodeLocation(decodeXmlText(text));
            } else if (key === 'Application Version') {
              applicationVersion = decodeXmlText(text);
            }
            break;

          case 'track':
            assignTrackField(top.frame.track, key, text, tag);
            break;

          case 'playlist':
            assignPlaylistField(top.frame.playlist, key, text, tag);
            break;

          case 'playlistItem':
            if (key === 'Track ID') {
              top.frame.playlist.trackIds.push(text.trim());
            }
            break;

          default:
            break;
        }
      }
    }

    if (pattern.lastIndex >= nextPause) {
      nextPause = pattern.lastIndex + sliceLength;
      yield Math.min(1, pattern.lastIndex / xml.length);
    }
  }

  return { musicFolder, applicationVersion, tracks, playlists };
}

/** Runs a parser generator straight through, ignoring the pauses. */
export function drainParser(parser: Generator<number, ItunesLibrary, void>) {
  let step = parser.next();

  while (!step.done) {
    step = parser.next();
  }

  return step.value;
}

/**
 * The parse as the UI wants it: awaitable, and reporting progress without
 * freezing the tab.
 *
 * `setTimeout` and not a microtask on purpose. A resolved promise runs before
 * the browser paints, so awaiting one would report progress nobody ever sees
 * and still block for the whole parse. A timeout yields to the event loop for
 * real, which is what lets the bar move and a click land.
 */
export function parseItunesLibraryAsync(
  xml: string,
  onProgress?: (fraction: number) => void,
): Promise<ItunesLibrary> {
  return new Promise((resolve, reject) => {
    const parser = parseItunesLibrary(xml);

    function step() {
      try {
        const next = parser.next();

        if (next.done) {
          resolve(next.value);
          return;
        }

        if (onProgress) {
          onProgress(next.value);
        }

        setTimeout(step, 0);
      } catch (error) {
        reject(error);
      }
    }

    step();
  });
}

/**
 * Reads a picked file as text.
 *
 * `FileReader` rather than `File.text()`: the latter is a 2020 API and this app
 * still has to run on whatever the iPad has (docs/MOBILE-IOS.md).
 */
export function readFileAsText(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
    reader.readAsText(file);
  });
}
